"use client"

import type React from "react"

import { useState, useEffect, useRef } from "react"
import { useRouter } from "next/router"
import { ArrowRight, Check, ChevronDown, Cpu, Globe, Lock, Sparkles, Wand2 } from "lucide-react"

import type { ImageModel } from "@/types/logo"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useLogoState } from "@/lib/use-logo-state"
import { useAuth } from "@/components/auth-provider"
import { cn } from "@/lib/utils"
import { logger } from "@/lib/logger"

const MODEL_LABELS: Record<ImageModel, string> = {
  openai: "OpenAI",
  "openai-gpt-image-2": "GPT Image 2",
  "gemini-nano-banana-pro": "Nano Banana Pro",
}

const EXAMPLES = [
  "A speciality coffee roaster in Lisbon, warm and unfussy",
  "A cybersecurity firm for hospitals, serious and precise",
  "A weekend surf school for beginners on the Atlantic coast",
]

export function LogoInput() {
  const router = useRouter()
  const { user, loading: authLoading } = useAuth()
  const { businessInfo, updateBusinessInfo, logoOptions, setLogoOptions, isLoaded } = useLogoState()

  const [prompt, setPrompt] = useState("")
  const [hydrated, setHydrated] = useState(false)
  const [isEnhancing, setIsEnhancing] = useState(false)
  const [visibility, setVisibility] = useState<"public" | "private">("public")
  const [imageModel, setImageModel] = useState<ImageModel>("openai")
  const [nanoBananaProEnabled, setNanoBananaProEnabled] = useState(false)
  const [gptImage2Enabled, setGptImage2Enabled] = useState(false)

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const isSubscriber = !!user?.isSubscriber
  const isAdmin = !!user?.isAdmin
  const canGoPrivate = isSubscriber || isAdmin

  // Which premium models this account may pick.
  const modelUnlocked = (model: ImageModel) => {
    if (model === "openai") return true
    if (model === "openai-gpt-image-2") return gptImage2Enabled || canGoPrivate
    return nanoBananaProEnabled || canGoPrivate
  }

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch("/api/app-settings")
        if (response.ok) {
          const settings = await response.json()
          setNanoBananaProEnabled(!!settings.nanoBananaProEnabled)
          setGptImage2Enabled(!!settings.gptImage2Enabled)
        }
      } catch (error) {
        logger.error("Error fetching app settings:", error)
      }
    }
    fetchSettings()
  }, [])

  // Restore the saved draft once the browser copy has been read back.
  useEffect(() => {
    if (!isLoaded || hydrated) return
    setPrompt(businessInfo.prompt || "")
    setVisibility(businessInfo.isPrivate ? "private" : "public")
    if (logoOptions.imageModel) setImageModel(logoOptions.imageModel)
    setHydrated(true)
  }, [isLoaded, hydrated, businessInfo.prompt, businessInfo.isPrivate, logoOptions.imageModel])

  // Keep the draft saved as it is typed, so a refresh never loses it.
  useEffect(() => {
    if (!hydrated) return
    const timeout = setTimeout(() => {
      updateBusinessInfo({ ...businessInfo, prompt })
    }, 400)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt, hydrated])

  // Grow the field with its content.
  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = "auto"
    textarea.style.height = `${Math.max(textarea.scrollHeight, 128)}px`
  }, [prompt])

  const selectVisibility = (value: "public" | "private") => {
    if (value === "private" && !canGoPrivate) {
      router.push("/pricing")
      return
    }
    setVisibility(value)
  }

  const selectModel = (value: ImageModel) => {
    if (!modelUnlocked(value)) {
      router.push("/pricing")
      return
    }
    setImageModel(value)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = prompt.trim()
    if (!trimmed) return

    updateBusinessInfo({
      ...businessInfo,
      prompt: trimmed,
      name: businessInfo.name || "",
      isPrivate: visibility === "private",
    })
    setLogoOptions({ ...logoOptions, imageModel })
    router.push("/brand-details")
  }

  const enhancePrompt = async () => {
    const original = prompt.trim()
    if (!original || isEnhancing) return

    setIsEnhancing(true)
    try {
      const response = await fetch("/api/enhance-prompt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: original }),
      })

      if (!response.ok) throw new Error(`Request failed (${response.status})`)

      const data = await response.json()
      if (data.enhancedPrompt) setPrompt(data.enhancedPrompt)
    } catch (error) {
      logger.error("Error enhancing prompt:", error)
    } finally {
      setIsEnhancing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto w-full max-w-2xl">
      <div
        className={cn(
          "relative rounded-xl border border-line bg-surface shadow-md",
          "transition-[border-color,box-shadow] duration-base ease-out",
          "focus-within:border-ink"
        )}
      >
        <label htmlFor="logo-brief" className="sr-only">
          Describe your business
        </label>
        <textarea
          id="logo-brief"
          ref={textareaRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe your business in a sentence. For example: a speciality coffee roaster in Lisbon, warm and unfussy."
          rows={2}
          required
          className="min-h-24 w-full resize-none rounded-xl bg-transparent px-4 pb-14 pt-4 text-body-lg text-ink outline-none placeholder:text-ink-subtle sm:min-h-32 sm:px-5 sm:pb-16 sm:pt-5"
        />

        <div className="absolute inset-x-3 bottom-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {/* Visibility */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line px-2.5 text-caption font-medium text-ink-muted transition-colors duration-fast ease-out hover:border-ink hover:text-ink"
                >
                  {visibility === "public" ? <Globe className="size-3.5" aria-hidden /> : <Lock className="size-3.5" aria-hidden />}
                  <span className="hidden sm:inline">{visibility === "public" ? "Public" : "Private"}</span>
                  <ChevronDown className="size-3" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel className="type-eyebrow">Who can see it</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => selectVisibility("public")} className="gap-3">
                  <Globe className="size-4 shrink-0" aria-hidden />
                  <span className="flex flex-1 flex-col">
                    <span className="text-small">Public</span>
                    <span className="text-caption text-ink-muted">Shown in the community gallery</span>
                  </span>
                  {visibility === "public" && <Check className="size-4 text-signal" aria-hidden />}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => selectVisibility("private")} className="gap-3">
                  <Lock className="size-4 shrink-0" aria-hidden />
                  <span className="flex flex-1 flex-col">
                    <span className="text-small">Private</span>
                    <span className="text-caption text-ink-muted">
                      {canGoPrivate ? "Only you can see it" : "Included with a subscription"}
                    </span>
                  </span>
                  {visibility === "private" && <Check className="size-4 text-signal" aria-hidden />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Model */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line px-2.5 text-caption font-medium text-ink-muted transition-colors duration-fast ease-out hover:border-ink hover:text-ink"
                >
                  {imageModel === "openai" ? <Cpu className="size-3.5" aria-hidden /> : <Wand2 className="size-3.5" aria-hidden />}
                  <span className="hidden sm:inline">{MODEL_LABELS[imageModel]}</span>
                  <ChevronDown className="size-3" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel className="type-eyebrow">Design engine</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(Object.keys(MODEL_LABELS) as ImageModel[]).map((model) => (
                  <DropdownMenuItem key={model} onSelect={() => selectModel(model)} className="gap-3">
                    {model === "openai" ? <Cpu className="size-4 shrink-0" aria-hidden /> : <Wand2 className="size-4 shrink-0" aria-hidden />}
                    <span className="flex flex-1 flex-col">
                      <span className="text-small">{MODEL_LABELS[model]}</span>
                      <span className="text-caption text-ink-muted">
                        {model === "openai"
                          ? "Reliable all-rounder"
                          : modelUnlocked(model)
                            ? "Sharper detail, slower"
                            : "Included with a subscription"}
                      </span>
                    </span>
                    {imageModel === model && <Check className="size-4 text-signal" aria-hidden />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={enhancePrompt}
              disabled={!prompt.trim() || isEnhancing}
              variant="outline"
              size="icon-sm"
              aria-label="Improve this description with AI"
              title="Improve this description"
            >
              <Sparkles className={cn(isEnhancing && "animate-pulse")} aria-hidden />
            </Button>
            <Button
              type="submit"
              disabled={!prompt.trim() || authLoading}
              size="icon-sm"
              aria-label="Start designing"
            >
              <ArrowRight aria-hidden />
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        <span className="text-caption font-medium text-ink">Try:</span>
        {EXAMPLES.map((example) => (
          <button
            key={example}
            type="button"
            onClick={() => setPrompt(example)}
            className="rounded-full border border-line bg-surface/90 px-3 py-1 text-caption text-ink backdrop-blur-sm transition-colors duration-fast ease-out hover:border-ink hover:text-ink"
          >
            {example.split(",")[0]}
          </button>
        ))}
      </div>
    </form>
  )
}
